/**
 * PB-Detail · Stundenplan-Tab (US-005)
 *
 * Allgemeingültiger **Tagesablauf** eines Pflegebedürftigen (PB). Kein
 * Wochenraster mehr — der Plan beschreibt einen generischen Tag.
 *
 * Die Seite ist zweigeteilt:
 *   • links  — Tagesübersicht (Summe der geplanten Zeiten, fixe 2 h Pause,
 *              Reserve-Zeit für spontane Leistung)
 *   • rechts — die Einträge: jeder Eintrag besteht aus einer Tätigkeit
 *              (Dropdown), Uhrzeit (von–bis) und optionaler Notiz.
 *
 * Rechts lassen sich die Einträge per Griff (⠿) nach oben/unten ziehen
 * (Tastatur: Pfeil hoch/runter) — die neue Reihenfolge verschiebt sie zeitlich
 * (`reorderBlocks`). Aufgeklappte Einträge klappen auch über ihren schraffierten
 * Kopfbereich wieder zu.
 *
 * Regeln:
 *   • 2 Stunden sind fest als Pause eingeplant (nicht editierbar).
 *   • Die Summe der geplanten Tätigkeiten darf 7 h nicht überschreiten.
 *   • Die Reserve-Zeit = 7 h − Summe der geplanten Tätigkeiten (frei,
 *     noch nicht verplant).
 *
 * Persistenz (unverändertes v2-Backend, Voll-Replace):
 *   GET /v2/admin/users/{id}/schedule
 *   PUT /v2/admin/users/{id}/schedule
 * Alle Blöcke werden mit weekday=1 gespeichert (generischer Tag).
 *
 * `validateBlocks` / `totalPlannedMinutes` sind als reine Funktionen
 * exportiert und werden in den Unit-Tests isoliert geprüft.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '../../../../components/ui';
import { BRAND, getThemeColors } from '../../../../lib/theme';
import { useIsMobile } from '../../../../lib/useBreakpoint';
import { getPbSchedule, putPbSchedule, type PbScheduleBlock } from '../../../../lib/api';
import { usePermission } from '../../../../contexts/PermissionsContext';
import { ScheduleLinkPanel } from '../ScheduleLinkPanel';
import type { V1User } from '../../../../lib/v1';
import {
  SCHEDULE_WEEKDAY,
  DAY_ACTIVITY_BUDGET_MIN,
  DAY_END_MIN,
  PB_ACTIVITIES,
  activityLabel,
  defaultPauseBlock,
  fmtClock,
  timeToMinutes,
  blockMinutes,
  layoutBlocks,
  isPauseBlock,
  plannedActivityMinutes,
  pauseMinutes,
  hasFixedDuration,
  nextEntryTimes,
  autoEndFor,
  endForActivitySelection,
  rangeOverlaps,
  movedRange,
  gridDraftRange,
  validateBlocks,
  cascadeFromEndChange,
  dropBlockRange,
  minuteFromFraction,
  firstInvalidBlockIndex,
  firstMissingNoteIndex,
  reorderBlocks,
  initialScrollMin,
  isNoteRequired,
  validateEntryDraft,
  NOTE_MAX_LEN,
  type ScheduleValidationError,
} from './scheduleUtils';
import { TimeInput24 } from '../../../../components/DateTimeInput';
import { ActivityPalette, pointInElement, PALETTE_STICKY_Z, type TilePoint } from './ActivityPalette';
import { ScheduleEntryDrawer, entryDraftErrorMessage, type EntryDraft } from './ScheduleEntryDrawer';

export * from './scheduleUtils';

/* ── Props ────────────────────────────────────────────────────────────────── */

interface Props {
  user: V1User | null;
  dark: boolean;
}

/** Draft-Block trägt einen stabilen Client-Key für React-Listen. */
type DraftBlock = PbScheduleBlock & { _key: string };

/** Zieh-Zustand im Zeitraster: Bereich aufziehen, Block verschieben oder Dauer ändern. */
type DragState =
  | { mode: 'create'; anchor: number; cur: number }
  | { mode: 'move'; key: string; blockStart: number; blockEnd: number; anchor: number; cur: number; startY: number; moved: boolean }
  | { mode: 'resize'; key: string; blockStart: number; blockEnd: number; cur: number; startY: number; moved: boolean };

let keySeq = 0;
function nextKey(): string {
  keySeq += 1;
  return `b${keySeq}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Höhe einer Stunde im Zeitraster. */
const PX_PER_HOUR = 52;

/* ── Anzeige-Helfer ──────────────────────────────────────────────────────── */

/** Minuten → "X Std YY Min" (deutsche Anzeige). */
function fmtHm(min: number): string {
  const m = Math.max(0, Math.round(min));
  const h = Math.floor(m / 60);
  const r = m % 60;
  return `${h} Std ${String(r).padStart(2, '0')} Min`;
}

/* ── Komponente ──────────────────────────────────────────────────────────── */

export function PbScheduleForm({ user, dark }: Props) {
  const tc = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const isMobile = useIsMobile();
  const paletteRef = useRef<HTMLDivElement>(null);
  const [paletteMaxH, setPaletteMaxH] = useState<number | null>(null);
  const canEditLocked = usePermission('pb.schedule.edit_locked');
  const [blocks, setBlocks] = useState<DraftBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Desktop: maximale Palettenhöhe = sichtbarer Ausschnitt des scrollenden Vorfahren.
  useEffect(() => {
    const el = paletteRef.current;
    if (isMobile || !el) { setPaletteMaxH(null); return undefined; }
    let sc: HTMLElement | null = null;
    for (let n = el.parentElement; n; n = n.parentElement) {
      const oy = getComputedStyle(n).overflowY;
      if (oy === 'auto' || oy === 'scroll') { sc = n; break; }
    }
    const measure = () => {
      const h = sc ? sc.clientHeight : window.innerHeight;
      setPaletteMaxH(h > 0 ? Math.max(120, h - 8) : null);
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = typeof ResizeObserver !== 'undefined' && sc ? new ResizeObserver(measure) : null;
    if (ro && sc) ro.observe(sc);
    return () => { window.removeEventListener('resize', measure); ro?.disconnect(); };
  }, [isMobile, loading, loadError]);
  const [formError, setFormError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  // US-006: vom Kunden über den öffentlichen Link festgelegt → nur mit
  // Sonder-Berechtigung editierbar.
  const [locked, setLocked] = useState(false);
  const editingBlocked = locked && !canEditLocked;

  // US-005: direktes Hinzufügen/Verschieben im Zeitraster der Tagesübersicht.
  const gridRef = useRef<HTMLDivElement>(null);
  const regionRef = useRef<HTMLDivElement>(null);
  const didInitialScroll = useRef(false);
  // In dieser Sitzung neu angelegte Einträge (ohne Speichern am Server) → Beschreibung Pflicht.
  const newKeysRef = useRef<Set<string>>(new Set());
  // Drawer zum Anlegen/Bearbeiten (Beschreibung ist bei neuen Einträgen Pflicht).
  const [draft, setDraft] = useState<EntryDraft | null>(null);
  const [drawerError, setDrawerError] = useState<string | null>(null);
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [drag, setDrag] = useState<DragState | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  // Im Raster angeklickter Eintrag → zugehörige Karte rechts hervorheben.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // Nur der gerade bearbeitete Eintrag ist rechts aufgeklappt; alle anderen
  // zeigen kompakt Tätigkeit, Start und Dauer.
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  // Kachel, die gerade über das Raster gezogen wird (Vorschau beim Ablegen).
  const [paletteDrag, setPaletteDrag] = useState<string | null>(null);
  const [dropMin, setDropMin] = useState<number | null>(null);
  // Eintrag rechts, der gerade per Griff in der Liste verschoben wird
  // (`over` = Zielposition in der sortierten Liste).
  const [listDrag, setListDrag] = useState<{ key: string; from: number; over: number } | null>(null);

  const applyResponse = useCallback((resBlocks: PbScheduleBlock[]) => {
    newKeysRef.current = new Set();
    setBlocks(resBlocks.map(b => ({ ...b, _key: nextKey() })));
  }, []);

  useEffect(() => {
    if (!user?.id) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    setLoadError(null);
    getPbSchedule(user.id)
      .then(res => {
        if (!alive) return;
        const loaded = res.blocks ?? [];
        // Leerer Tagesplan → Pause 13:00–15:00 vorbelegen (wird erst mit
        // dem nächsten Speichern persistiert).
        applyResponse(loaded.length > 0 ? loaded : [defaultPauseBlock()]);
      })
      .catch(() => { if (alive) setLoadError(tr('pflege.schedule.loadError')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [user?.id, applyResponse, tr]);

  const updateBlock = useCallback((key: string, patch: Partial<PbScheduleBlock>) => {
    setBlocks(prev => prev.map(b => (b._key === key ? { ...b, ...patch } : b)));
    setFormError(null);
    setSavedAt(null);
  }, []);

  // Tätigkeit wechseln — Bis-Zeit aus Standard-Dauer der Tätigkeit vorbelegen
  // (Pause/Medikamente fix, freie Tätigkeiten mit Standard-Dauer editierbar).
  const setActivity = useCallback((key: string, careType: string) => {
    setBlocks(prev => prev.map(b => {
      if (b._key !== key) return b;
      const end = endForActivitySelection(careType || null, b.start_time);
      return { ...b, care_type: careType || null, end_time: end ?? b.end_time };
    }));
    setFormError(null);
    setSavedAt(null);
  }, []);

  // Startzeit ändern — bei fixer Dauer Bis-Zeit nachführen.
  const setStart = useCallback((key: string, start: string) => {
    setBlocks(prev => prev.map(b => {
      if (b._key !== key) return b;
      const auto = autoEndFor(b.care_type, start);
      return { ...b, start_time: start, end_time: auto ?? b.end_time };
    }));
    setFormError(null);
    setSavedAt(null);
  }, []);

  // Bis-Zeit ändern (US-WP10 / #24, erweitert) — der FOLGENDE Eintrag und alle
  // danach beginnenden rücken um ein signiertes Delta mit.
  const setEnd = useCallback((key: string, end: string) => {
    setBlocks(prev => cascadeFromEndChange(prev, key, end));
    setFormError(null);
    setSavedAt(null);
  }, []);

  const removeBlock = useCallback((key: string) => {
    setBlocks(prev => prev.filter(b => b._key !== key));
    setFormError(null);
    setSavedAt(null);
  }, []);

  // Tagesübersicht als absolut positionierte Boxen — jeder Block bekommt GENAU
  // eine sichtbare Box, unabhängig von einer Rasterauflösung.
  const layout = useMemo(() => layoutBlocks(blocks), [blocks]);
  // Freie (nicht verplante) Bereiche des Zeitfensters — als hellblau
  // schraffierte Boxen hinter den echten Blöcken.
  const gapBoxes = useMemo(() => {
    const span = Math.max(1, layout.windowEndMin - layout.windowStartMin);
    const covered = layout.boxes
      .map(b => ({ s: b.startMin, e: b.endMin }))
      .sort((a, b) => a.s - b.s);
    const gaps: Array<{ topPct: number; heightPct: number }> = [];
    let cursor = layout.windowStartMin;
    for (const r of covered) {
      const s = Math.max(cursor, layout.windowStartMin);
      if (r.s > s) {
        gaps.push({ topPct: ((s - layout.windowStartMin) / span) * 100, heightPct: ((r.s - s) / span) * 100 });
      }
      cursor = Math.max(cursor, r.e);
    }
    if (cursor < layout.windowEndMin) {
      gaps.push({ topPct: ((cursor - layout.windowStartMin) / span) * 100, heightPct: ((layout.windowEndMin - cursor) / span) * 100 });
    }
    return gaps.filter(g => g.heightPct > 0);
  }, [layout.boxes, layout.windowStartMin, layout.windowEndMin]);

  // Volle Stunden als Achsen-Marken.
  const hourMarks = useMemo(() => {
    const marks: number[] = [];
    for (let m = layout.windowStartMin; m <= layout.windowEndMin; m += 60) marks.push(m);
    return marks;
  }, [layout.windowStartMin, layout.windowEndMin]);

  // Raster (00–24 Uhr) beim ersten Anzeigen auf eine sinnvolle Stelle scrollen:
  // eine Stunde vor dem ersten Eintrag (ohne Pause), sonst 06:00.
  useEffect(() => {
    if (loading || didInitialScroll.current) return;
    const region = regionRef.current;
    if (!region) return;
    didInitialScroll.current = true;
    region.scrollTop = ((initialScrollMin(blocks) - layout.windowStartMin) / 60) * PX_PER_HOUR;
  }, [loading, blocks, layout.windowStartMin]);

  // ── Hinzufügen/Verschieben direkt im Zeitraster ────────────────────────────
  /** Drawer für einen neuen Eintrag öffnen (Tätigkeit optional vorbelegt). */
  const openNewDraft = useCallback((careType: string | null, startMin: number, endMin: number) => {
    setDraft({
      _key: null, care_type: careType, start_time: fmtClock(startMin), end_time: fmtClock(endMin),
      note: null, originalNote: null,
    });
    setDrawerError(null);
    setMoveError(null);
    setFormError(null);
  }, []);

  /** „Tätigkeit hinzufügen": Drawer mit Zeit nach dem letzten Eintrag. */
  const addBlock = useCallback(() => {
    const { start_time, end_time } = nextEntryTimes(blocks);
    openNewDraft('', timeToMinutes(start_time), timeToMinutes(end_time));
  }, [blocks, openNewDraft]);

  /** Neuen Eintrag im Raster anlegen (Start..Ende): Überschneidung prüfen, dann Drawer. */
  const addBlockAt = useCallback((startMin: number, endMin: number) => {
    if (rangeOverlaps(startMin, endMin, blocks)) {
      setMoveError(tr('pflege.schedule.overlapError'));
      return;
    }
    openNewDraft('', startMin, endMin);
  }, [blocks, openNewDraft, tr]);

  /** Tätigkeit aus einer Kachel an Minute `startMin` anlegen (Standard-Dauer), überschneidungsfrei. */
  const addActivityAt = useCallback((careType: string, startMin: number) => {
    const { start, end } = dropBlockRange(careType, startMin);
    if (rangeOverlaps(start, end, blocks)) {
      setMoveError(tr('pflege.schedule.dropOverlapError'));
      return;
    }
    openNewDraft(careType, start, end);
  }, [blocks, openNewDraft, tr]);

  /** Kachel angeklickt (Touch/Tastatur): direkt nach dem letzten Eintrag anlegen. */
  const pickActivity = useCallback((careType: string) => {
    addActivityAt(careType, timeToMinutes(nextEntryTimes(blocks).start_time));
  }, [addActivityAt, blocks]);

  /** Vorhandenen Eintrag im Drawer bearbeiten. */
  const openEditDraft = useCallback((b: DraftBlock) => {
    setDraft({
      _key: b._key, care_type: b.care_type, start_time: b.start_time, end_time: b.end_time,
      note: b.note, originalNote: b.note,
    });
    setDrawerError(null);
    setSelectedKey(b._key);
    setMoveError(null);
  }, []);

  const closeDrawer = useCallback(() => { setDraft(null); setDrawerError(null); }, []);
  const draftSetActivity = useCallback((careType: string) => {
    setDraft(d => {
      if (!d) return d;
      const end = endForActivitySelection(careType || null, d.start_time);
      return { ...d, care_type: careType || null, end_time: end ?? d.end_time };
    });
    setDrawerError(null);
  }, []);
  const draftSetStart = useCallback((start: string) => {
    setDraft(d => {
      if (!d) return d;
      const auto = autoEndFor(d.care_type, start);
      return { ...d, start_time: start, end_time: auto ?? d.end_time };
    });
    setDrawerError(null);
  }, []);
  const draftPatch = useCallback((patch: Partial<EntryDraft>) => {
    setDraft(d => (d ? { ...d, ...patch } : d));
    setDrawerError(null);
  }, []);
  const draftNoteRequired = draft
    ? isNoteRequired({ careType: draft.care_type, isNew: draft._key === null, originalNote: draft.originalNote })
    : false;

  /** Entwurf übernehmen: neuer Eintrag kommt erst jetzt (mit Beschreibung) in den Plan. */
  const saveDraft = useCallback(() => {
    if (!draft) return;
    const others = blocks.filter(b => b._key !== draft._key);
    const code = validateEntryDraft(draft, others, draftNoteRequired);
    if (code) { setDrawerError(entryDraftErrorMessage(code, tr)); return; }
    const note = draft.note?.trim() ? draft.note.trim() : null;
    const key = draft._key ?? nextKey();
    if (draft._key) {
      setBlocks(prev => prev.map(b => (b._key === key
        ? { ...b, start_time: draft.start_time, end_time: draft.end_time, care_type: draft.care_type, note }
        : b)));
    } else {
      newKeysRef.current.add(key);
      setBlocks(prev => [...prev, {
        _key: key, weekday: SCHEDULE_WEEKDAY,
        start_time: draft.start_time, end_time: draft.end_time, care_type: draft.care_type, note,
      }]);
    }
    setSelectedKey(key);
    setDraft(null);
    setDrawerError(null);
    setMoveError(null);
    setFormError(null);
    setSavedAt(null);
  }, [draft, blocks, draftNoteRequired, tr]);

  const deleteDraft = useCallback(() => {
    if (draft?._key) removeBlock(draft._key);
    setDraft(null);
    setDrawerError(null);
  }, [draft, removeBlock]);

  /** Bestehenden Block um delta verschieben (Dauer bleibt, überschneidungsfrei). */
  const moveBlock = useCallback((key: string, blockStart: number, blockEnd: number, deltaMin: number) => {
    setBlocks(prev => {
      const idx = prev.findIndex(b => b._key === key);
      if (idx < 0) return prev;
      const { start, end } = movedRange(blockStart, blockEnd, deltaMin);
      if (rangeOverlaps(start, end, prev, idx)) {
        setMoveError(tr('pflege.schedule.moveOverlapError'));
        return prev;
      }
      setMoveError(null);
      return prev.map((b, i) => (i === idx ? { ...b, start_time: fmtClock(start), end_time: fmtClock(end) } : b));
    });
    setFormError(null);
    setSavedAt(null);
  }, [tr]);

  /** Dauer eines Blocks per Zieh-Handle ändern — Folge-Einträge rücken lückenlos mit. */
  const resizeBlockEnd = useCallback((key: string, newEndMin: number) => {
    setBlocks(prev => cascadeFromEndChange(prev, key, fmtClock(newEndMin)));
    setFormError(null);
    setSavedAt(null);
  }, []);

  /** Eintrag rechts an eine andere Position der Liste schieben → zeitlich verschieben. */
  const reorderBlock = useCallback((key: string, toIndex: number) => {
    setBlocks(prev => reorderBlocks(prev, key, toIndex));
    setSelectedKey(key);
    setMoveError(null);
    setFormError(null);
    setSavedAt(null);
  }, []);

  // Minute (auf 5-Min-Schritt gerundet) aus der vertikalen Mausposition über
  // dem Zeitraster.
  const GRID_STEP_MIN = 5;
  const MOVE_THRESHOLD_PX = 5;
  const minFromY = useCallback((clientY: number): number | null => {
    const el = gridRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    if (r.height <= 0) return null;
    return minuteFromFraction((clientY - r.top) / r.height, layout.windowStartMin, layout.windowEndMin, GRID_STEP_MIN);
  }, [layout.windowStartMin, layout.windowEndMin]);

  // ── Kachel aus der Palette ins Raster ziehen ───────────────────────────────
  const onTileDrag = useCallback((_careType: string, p: TilePoint) => {
    if (editingBlocked) return;
    setDropMin(pointInElement(gridRef.current, p) ? minFromY(p.y) : null);
  }, [editingBlocked, minFromY]);
  const onTileDrop = useCallback((careType: string, p: TilePoint) => {
    setDropMin(null);
    setPaletteDrag(null);
    if (editingBlocked || !pointInElement(gridRef.current, p)) return;
    const m = minFromY(p.y);
    if (m !== null) addActivityAt(careType, m);
  }, [editingBlocked, minFromY, addActivityAt]);

  const blockAtMin = useCallback((min: number): DraftBlock | null => {
    return blocks.find(b => {
      const s = timeToMinutes(b.start_time), e = timeToMinutes(b.end_time);
      return !Number.isNaN(s) && !Number.isNaN(e) && min >= s && min < e;
    }) ?? null;
  }, [blocks]);

  const onGridMouseDown = useCallback((e: React.MouseEvent) => {
    if (editingBlocked) return;
    const m = minFromY(e.clientY);
    if (m === null) return;
    e.preventDefault();
    setMoveError(null);
    // Der ganze Eintrag ist anklickbar: erst nach dem angeklickten Element
    // suchen. Die Uhrzeit allein verfehlt Einträge, deren Box größer
    // gezeichnet ist als ihre Dauer (Mindesthöhe kurzer Einträge).
    const hitKey = (e.target as HTMLElement | null)?.closest?.('[data-block-key]')?.getAttribute('data-block-key') ?? null;
    const b = (hitKey ? blocks.find(x => x._key === hitKey) : null) ?? blockAtMin(m);
    if (b) {
      setDrag({ mode: 'move', key: b._key, blockStart: timeToMinutes(b.start_time), blockEnd: timeToMinutes(b.end_time), anchor: m, cur: m, startY: e.clientY, moved: false });
    } else {
      setDrag({ mode: 'create', anchor: m, cur: m });
    }
  }, [editingBlocked, minFromY, blockAtMin, blocks]);

  const onGridMouseMove = useCallback((e: React.MouseEvent) => {
    if (!drag) return;
    const m = minFromY(e.clientY);
    // Erst ab ein paar Pixeln gilt es als Verschieben/Ziehen, sonst als Klick (Hand zittert).
    // Die Pixelschwelle gilt unabhängig davon, ob die Minute bestimmbar ist.
    setDrag(d => {
      if (!d) return d;
      if (d.mode === 'move' || d.mode === 'resize') {
        const moved = d.moved || Math.abs(e.clientY - d.startY) >= MOVE_THRESHOLD_PX;
        return m === null ? { ...d, moved } : { ...d, cur: m, moved };
      }
      return m === null ? d : { ...d, cur: m };
    });
  }, [drag, minFromY]);

  // Maus loslassen (auch außerhalb des Rasters) → Aktion ausführen.
  useEffect(() => {
    if (!drag) return;
    const onUp = () => {
      if (drag.mode === 'create') {
        const lo = Math.min(drag.anchor, drag.cur);
        const hi = Math.max(drag.anchor, drag.cur);
        const { start, end } = gridDraftRange(lo, hi);
        addBlockAt(start, end);
      } else if (drag.mode === 'resize') {
        if (!drag.moved) {
          // Klick ohne Bewegung auf den Anfasser: der ganze Termin ist anklickbar → Drawer.
          const b = blocks.find(x => x._key === drag.key);
          if (b) openEditDraft(b);
        } else {
          const newEnd = Math.max(drag.blockStart + GRID_STEP_MIN, Math.min(drag.cur, DAY_END_MIN));
          if (newEnd !== drag.blockEnd) resizeBlockEnd(drag.key, newEnd);
        }
      } else if (!drag.moved || drag.cur === drag.anchor) {
        const b = blocks.find(x => x._key === drag.key);
        if (b) openEditDraft(b);                     // reiner Klick → Drawer zum Bearbeiten
      } else {
        moveBlock(drag.key, drag.blockStart, drag.blockEnd, drag.cur - drag.anchor);
      }
      setDrag(null);
    };
    document.addEventListener('mouseup', onUp);
    return () => document.removeEventListener('mouseup', onUp);
  }, [drag, blocks, addBlockAt, moveBlock, openEditDraft, resizeBlockEnd]);

  // Live-Vorschau der Zieh-Auswahl.
  let dragPreview: { topPct: number; heightPct: number; bad: boolean } | null = null;
  if (drag) {
    const span = Math.max(1, layout.windowEndMin - layout.windowStartMin);
    let lo: number, hi: number, bad = false;
    if (drag.mode === 'create') {
      lo = Math.min(drag.anchor, drag.cur);
      hi = Math.max(drag.anchor, drag.cur);
      if (hi <= lo) hi = lo + 60;
      bad = rangeOverlaps(lo, hi, blocks);
    } else if (drag.mode === 'resize') {
      // Folge-Einträge rücken beim Loslassen automatisch mit (Kaskade) —
      // deshalb keine Overlap-Warnung während des Ziehens.
      lo = drag.blockStart;
      hi = Math.max(drag.blockStart + GRID_STEP_MIN, Math.min(drag.cur, DAY_END_MIN));
    } else {
      const { start, end } = movedRange(drag.blockStart, drag.blockEnd, drag.moved ? drag.cur - drag.anchor : 0);
      const idx = blocks.findIndex(b => b._key === drag.key);
      lo = start; hi = end;
      bad = rangeOverlaps(start, end, blocks, idx);
    }
    const topPct = Math.max(0, ((lo - layout.windowStartMin) / span) * 100);
    const heightPct = Math.max(1, ((hi - lo) / span) * 100);
    dragPreview = { topPct, heightPct: Math.min(heightPct, 100 - topPct), bad };
  } else if (paletteDrag && dropMin !== null) {
    const span = Math.max(1, layout.windowEndMin - layout.windowStartMin);
    const { start, end } = dropBlockRange(paletteDrag, dropMin);
    const topPct = Math.max(0, ((start - layout.windowStartMin) / span) * 100);
    const heightPct = Math.max(1, ((end - start) / span) * 100);
    dragPreview = { topPct, heightPct: Math.min(heightPct, 100 - topPct), bad: rangeOverlaps(start, end, blocks) };
  }

  const errorMessageFor = useCallback((err: ScheduleValidationError): string => {
    switch (err.code) {
      case 'INCOMPLETE':       return tr('pflege.schedule.incompleteError');
      case 'NO_ACTIVITY':      return tr('pflege.schedule.noActivityError');
      case 'END_BEFORE_START': return tr('pflege.schedule.endBeforeStartError');
      case 'OVERLAP':          return tr('pflege.schedule.overlapError');
      case 'OVERLIMIT':        return tr('pflege.schedule.overlimitError');
    }
  }, [tr]);

  const handleSave = useCallback(async () => {
    if (!user?.id) return;
    const plain: PbScheduleBlock[] = blocks.map(({ _key, ...b }) => ({ ...b, weekday: SCHEDULE_WEEKDAY }));
    const violation = validateBlocks(plain);
    if (violation) {
      setFormError(errorMessageFor(violation));
      // Den betroffenen Eintrag aufklappen, sonst ist er in der kompakten Liste nicht zu finden.
      const bad = firstInvalidBlockIndex(plain);
      if (bad >= 0) setExpandedKey(blocks[bad]._key);
      return;
    }
    // Neu angelegte Einträge brauchen eine Beschreibung (Drawer erzwingt sie; hier
    // der Rückfall, falls sie inline wieder geleert wurde). Bestehende Einträge
    // ohne Text bleiben speicherbar.
    const noNote = firstMissingNoteIndex(blocks, newKeysRef.current);
    if (noNote >= 0) {
      setFormError(tr('pflege.schedule.noteRequiredError'));
      setExpandedKey(blocks[noNote]._key);
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const res = await putPbSchedule(user.id, plain);
      applyResponse(res.blocks ?? []);
      setSavedAt(Date.now());
    } catch (err: unknown) {
      const e = err as { response?: { data?: { errors?: Array<{ code?: string }> } } };
      const codes = e?.response?.data?.errors ?? [];
      if (Array.isArray(codes) && codes.some(c => c?.code === 'E_SCHEDULE_OVERLAP')) {
        setFormError(tr('pflege.schedule.overlapError'));
      } else if (Array.isArray(codes) && codes.some(c => c?.code === 'E_SCHEDULE_OVERLIMIT')) {
        setFormError(tr('pflege.schedule.overlimitError'));
      } else {
        setFormError(tr('pflege.schedule.saveError'));
      }
    } finally {
      setSaving(false);
    }
  }, [user?.id, blocks, errorMessageFor, applyResponse, tr]);

  // Einträge nach Startzeit sortiert (Anzeige).
  const sortedBlocks = useMemo(
    () => [...blocks].sort((a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time)),
    [blocks],
  );

  // ── Einträge rechts per Griff umsortieren ──────────────────────────────────
  /** Zielposition aus der Zeigerhöhe: Anzahl der übrigen Karten, deren Mitte darüber liegt. */
  const listIndexAtY = useCallback((key: string, clientY: number): number => {
    let idx = 0;
    for (const b of sortedBlocks) {
      if (b._key === key) continue;
      const el = cardRefs.current.get(b._key);
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (clientY > r.top + r.height / 2) idx += 1;
    }
    return idx;
  }, [sortedBlocks]);

  const onHandlePointerDown = useCallback((e: React.PointerEvent<HTMLElement>, key: string, from: number) => {
    if (editingBlocked || (e.pointerType === 'mouse' && e.button !== 0)) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setMoveError(null);
    setListDrag({ key, from, over: from });
  }, [editingBlocked]);

  const onHandlePointerMove = useCallback((e: React.PointerEvent<HTMLElement>, key: string) => {
    if (!listDrag || listDrag.key !== key) return;
    const over = listIndexAtY(key, e.clientY);
    if (over !== listDrag.over) setListDrag({ ...listDrag, over });
  }, [listDrag, listIndexAtY]);

  const onHandlePointerUp = useCallback((e: React.PointerEvent<HTMLElement>, key: string) => {
    if (!listDrag || listDrag.key !== key) return;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    const over = listIndexAtY(key, e.clientY);
    setListDrag(null);
    if (over !== listDrag.from) reorderBlock(key, over);
  }, [listDrag, listIndexAtY, reorderBlock]);

  const onHandleKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>, key: string, from: number) => {
    if (editingBlocked) return;
    const to = e.key === 'ArrowUp' ? from - 1 : e.key === 'ArrowDown' ? from + 1 : null;
    if (to === null) return;
    e.preventDefault();
    if (to >= 0 && to < sortedBlocks.length) reorderBlock(key, to);
  }, [editingBlocked, sortedBlocks.length, reorderBlock]);

  const plannedMin = useMemo(() => plannedActivityMinutes(blocks), [blocks]);
  const pauseMin = useMemo(() => pauseMinutes(blocks), [blocks]);
  const reserveMin = Math.max(0, DAY_ACTIVITY_BUDGET_MIN - plannedMin);
  const overLimit = plannedMin > DAY_ACTIVITY_BUDGET_MIN;
  const pct = Math.min(100, Math.round((plannedMin / DAY_ACTIVITY_BUDGET_MIN) * 100));

  const inputStyle: React.CSSProperties = {
    fontSize: 13, padding: '8px 10px', border: `1px solid ${tc.border}`, borderRadius: 8,
    background: tc.inputBg, color: tc.text, fontFamily: 'Outfit,sans-serif',
    outline: 'none', boxSizing: 'border-box',
  };

  /* ── Linke Tagesübersicht (Stundenplan-Tabelle) ──────────────────────── */
  const SummaryChip = ({ label, value, accent }: { label: string; value: string; accent?: string }) => (
    <div style={{ flex: 1, minWidth: 84, background: tc.cardMuted, borderRadius: 8, padding: '7px 10px' }}>
      <div style={{ fontSize: 10.5, color: tc.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</div>
      <div style={{ fontSize: 14, color: accent ?? tc.heading, fontWeight: 700, whiteSpace: 'nowrap' }}>{value}</div>
    </div>
  );

  const windowSpanMin = layout.windowEndMin - layout.windowStartMin;
  const gridHeightPx = (windowSpanMin / 60) * PX_PER_HOUR;
  const AXIS_W = 52;

  const overview = (
    <Card dark={dark} style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, alignSelf: 'flex-start', position: isMobile ? undefined : 'sticky', top: 0, maxHeight: isMobile ? undefined : 'calc(100vh - 96px)', minHeight: 0 }}>
      <div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, color: tc.heading, fontSize: 16, fontWeight: 700 }}>{tr('pflege.schedule.overview')}</h3>
        </div>
        <p style={{ margin: '4px 0 0', color: tc.muted, fontSize: 12 }}>
          {editingBlocked ? tr('pflege.schedule.overviewHint') : tr('pflege.schedule.gridHint')}
        </p>
      </div>

      {/* Kompakte Kennzahlen + Fortschritt gegen das 6,6-h-Budget */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <SummaryChip label={tr('pflege.schedule.planned')} value={fmtHm(plannedMin)} accent={overLimit ? '#b42318' : undefined} />
        <SummaryChip label={tr('pflege.schedule.reserve')} value={fmtHm(reserveMin)} accent={BRAND} />
        <SummaryChip label={tr('pflege.schedule.break')} value={fmtHm(pauseMin)} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: tc.muted }}>
          <span>{tr('pflege.schedule.planned')}</span>
          <span style={{ color: overLimit ? '#b42318' : tc.text, fontWeight: 700 }}>{fmtHm(plannedMin)} / {fmtHm(DAY_ACTIVITY_BUDGET_MIN)}</span>
        </div>
        <div style={{ height: 7, borderRadius: 6, background: tc.cardMuted, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: overLimit ? '#f04438' : BRAND, transition: 'width 0.15s' }} />
        </div>
      </div>

      {overLimit && (
        <div role="alert" style={{ padding: '8px 10px', background: dark ? '#1a0505' : '#fff5f5', border: '1px solid #fda29b', borderRadius: 8, fontSize: 12, color: '#b42318' }}>
          {tr('pflege.schedule.overlimitError')}
        </div>
      )}

      {moveError && (
        <div role="alert" style={{ padding: '8px 10px', background: dark ? '#1a0505' : '#fff5f5', border: '1px solid #fda29b', borderRadius: 8, fontSize: 12, color: '#b42318' }}>
          {moveError}
        </div>
      )}

      {/* Tages-Zeitraster */}
      <div
        ref={regionRef}
        role="region"
        aria-label={tr('pflege.schedule.overview')}
        style={{
          border: `1px solid ${tc.border}`, borderRadius: 10,
          overflowX: 'hidden', overflowY: 'auto',
          minHeight: isMobile ? 280 : 0,
          flex: isMobile ? undefined : '1 1 auto',
          maxHeight: isMobile ? 'calc(100vh - 230px)' : 'calc(100vh - 300px)',
        }}
      >
        {/* Padding oben/unten, damit die erste und letzte Stundenmarke (und
            damit der Startzeitpunkt) nicht am Rand abgeschnitten werden. */}
        <div style={{ display: 'flex', position: 'relative', paddingTop: 16, paddingBottom: 16 }}>
          {/* Zeitachse (Stundenmarken) */}
          <div style={{ width: AXIS_W, flexShrink: 0, position: 'relative', height: gridHeightPx, borderRight: `1px solid ${tc.borderSoft}` }}>
            {hourMarks.map(m => (
              <div
                key={m}
                style={{
                  position: 'absolute',
                  top: ((m - layout.windowStartMin) / 60) * PX_PER_HOUR,
                  left: 0, right: 4, transform: 'translateY(-50%)',
                  fontSize: 11, color: tc.text, fontWeight: 600, textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                }}
              >
                {fmtClock(m)}
              </div>
            ))}
          </div>

          {/* Blockspalte: Klick-/Zieh-Fläche + absolut positionierte Boxen */}
          <div
            ref={gridRef}
            data-testid="schedule-grid"
            onMouseDown={editingBlocked ? undefined : onGridMouseDown}
            onMouseMove={editingBlocked ? undefined : onGridMouseMove}
            style={{
              position: 'relative', flex: 1, height: gridHeightPx,
              userSelect: drag ? 'none' : undefined,
              cursor: editingBlocked ? 'default' : drag?.mode === 'move' ? 'grabbing' : 'pointer',
            }}
          >
            {/* Freie Zeiten (nicht verplant) */}
            {gapBoxes.map((g, i) => (
              <div
                key={`gap-${i}`}
                aria-hidden
                style={{
                  position: 'absolute', left: 0, right: 0,
                  top: `${g.topPct}%`, height: `${g.heightPct}%`,
                  background: 'repeating-linear-gradient(45deg, rgba(59,130,246,0.10) 0 6px, rgba(59,130,246,0.18) 6px 12px)',
                  pointerEvents: 'none', zIndex: 0,
                }}
              />
            ))}

            {/* Stundenlinien (Hintergrund) */}
            {hourMarks.map(m => (
              <div
                key={m}
                style={{
                  position: 'absolute', left: 0, right: 0,
                  top: ((m - layout.windowStartMin) / 60) * PX_PER_HOUR,
                  borderTop: `1px solid ${tc.borderSoft}`,
                }}
              />
            ))}

            {/* Zieh-Vorschau */}
            {dragPreview && (
              <div
                aria-hidden
                style={{
                  position: 'absolute', left: 2, right: 2,
                  top: `${dragPreview.topPct}%`, height: `${dragPreview.heightPct}%`,
                  borderRadius: 6,
                  background: dragPreview.bad
                    ? (dark ? 'rgba(240,68,56,0.30)' : 'rgba(240,68,56,0.18)')
                    : (dark ? 'rgba(175,0,124,0.22)' : 'rgba(175,0,124,0.12)'),
                  border: `1px dashed ${dragPreview.bad ? '#f04438' : BRAND}`,
                  pointerEvents: 'none',
                }}
              />
            )}

            {/* Block-Boxen — für jeden Block genau eine */}
            {layout.boxes.map(box => {
              const b = blocks[box.blockIndex];
              if (!b) return null;
              const dragging = drag?.mode === 'move' && drag.key === b._key;
              const selected = b._key === selectedKey;
              const pause = isPauseBlock(b);
              return (
                <div
                  key={b._key}
                  data-block-key={b._key}
                  role={editingBlocked ? undefined : 'button'}
                  tabIndex={editingBlocked ? undefined : 0}
                  onKeyDown={editingBlocked ? undefined : (ev => {
                    if (ev.target === ev.currentTarget && (ev.key === 'Enter' || ev.key === ' ')) { ev.preventDefault(); openEditDraft(b); }
                  })}
                  title={editingBlocked ? undefined : tr('pflege.schedule.gridBlockTitle')}
                  style={{
                    position: 'absolute', left: 2, right: 2,
                    top: `${box.topPct}%`, height: `${box.heightPct}%`,
                    minHeight: 18, boxSizing: 'border-box', overflow: 'hidden',
                    borderRadius: 6, padding: '2px 6px',
                    background: pause
                      ? 'repeating-linear-gradient(45deg, rgba(34,197,94,0.28) 0 7px, rgba(34,197,94,0.45) 7px 14px)'
                      : (dark ? 'rgba(175,0,124,0.16)' : 'rgba(175,0,124,0.08)'),
                    border: pause ? '1px solid #22c55e' : undefined,
                    borderLeft: `3px solid ${selected ? tc.heading : pause ? '#16a34a' : BRAND}`,
                    zIndex: 1,
                    boxShadow: selected ? `inset 0 0 0 1px ${BRAND}` : undefined,
                    opacity: dragging ? 0.55 : 1,
                    cursor: editingBlocked ? 'default' : dragging ? 'grabbing' : 'grab',
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 700, color: tc.heading, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {b.care_type ? activityLabel(b.care_type, tr) : tr('pflege.schedule.activityPlaceholder')}
                  </div>
                  <div style={{ fontSize: 10.5, color: tc.muted, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {b.start_time}–{b.end_time} · {fmtHm(blockMinutes(b))}
                  </div>
                  {b.note && <div style={{ fontSize: 10.5, color: tc.muted, fontStyle: 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.note}</div>}
                  {/* Resize-Handle an der Unterkante: Dauer per Ziehen ändern
                      (nicht bei fixer Dauer wie Pause/Medikamente). */}
                  {!editingBlocked && !hasFixedDuration(b.care_type) && (
                    <div
                      title={tr('pflege.schedule.resizeHandleTitle')}
                      onMouseDown={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        const s = timeToMinutes(b.start_time);
                        const en = timeToMinutes(b.end_time);
                        if (Number.isNaN(s) || Number.isNaN(en)) return;
                        setMoveError(null);
                        setSelectedKey(b._key);
                        setDrag({ mode: 'resize', key: b._key, blockStart: s, blockEnd: en, cur: en, startY: e.clientY, moved: false });
                      }}
                      style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 8, cursor: 'ns-resize' }}
                    >
                      <div style={{ margin: '3px auto 0', width: 26, height: 3, borderRadius: 2, background: selected ? tc.heading : BRAND, opacity: 0.55 }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Card>
  );

  // Die Auswahl bleibt beim Scrollen oben stehen. Am Handy als einzeilige Leiste
  // über dem Raster (die Liste liegt dort unter dem Raster), am Desktop am Kopf
  // der rechten Spalte (die Spalte ist so hoch wie die Zeile, daher haftet sie).
  // Die Höhe richtet sich nach dem sichtbaren Ausschnitt des scrollenden
  // Vorfahren (Tab-Inhalt unter Personen- und Tab-Leiste), NICHT nach 100vh:
  // eine höhere Palette würde am Ende der Spalte nach oben aus dem Ausschnitt
  // geschoben und Überschrift samt Hinweistext verschwänden unter der Kopfleiste.
  const stickyPalette = (
    <div
      ref={paletteRef}
      data-testid="palette-sticky"
      style={{
        position: 'sticky', top: 0, zIndex: PALETTE_STICKY_Z,
        background: tc.bg, paddingBottom: 4,
        maxHeight: isMobile ? 'calc(100vh - 16px)' : (paletteMaxH ?? 'calc(100vh - 16px)'),
        // Desktop: nicht die ganze Palette scrollen (sonst verschwindet die
        // Überschrift), sondern nur die Kachelliste darin.
        ...(isMobile ? { overflowY: 'visible' as const } : { overflowY: 'hidden' as const, display: 'flex', flexDirection: 'column' as const }),
      }}
    >
      <ActivityPalette
        dark={dark}
        variant={isMobile ? 'strip' : 'grid'}
        disabled={editingBlocked}
        onPick={pickActivity}
        onDragStateChange={ct => { setPaletteDrag(ct); if (!ct) setDropMin(null); }}
        onTileDrag={onTileDrag}
        onTileDrop={onTileDrop}
      />
    </div>
  );

  /* ── Rechte Eintrags-Liste ───────────────────────────────────────────── */
  const entries = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignSelf: 'stretch' }}>
      <Card dark={dark} style={{ padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0, color: tc.heading, fontSize: 18, fontWeight: 700 }}>{tr('pflege.schedule.title')}</h2>
            <p style={{ margin: '4px 0 0', color: tc.muted, fontSize: 12.5 }}>{tr('pflege.schedule.subtitle')}</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {savedAt !== null && (
              <span role="status" style={{ fontSize: 12.5, color: '#027a48', fontWeight: 600 }}>{tr('pflege.schedule.saved')}</span>
            )}
            <Button dark={dark} variant="primary" size="sm" disabled={saving || loading || !user?.id || editingBlocked} onClick={handleSave}>
              {saving ? tr('pflege.schedule.saving') : tr('pflege.schedule.save')}
            </Button>
          </div>
        </div>
        {locked && (
          <div role="status" style={{ marginTop: 12, padding: '9px 12px', background: dark ? '#0f1115' : '#f8fafc', border: `1px solid ${tc.border}`, borderRadius: 8, fontSize: 12.5, color: tc.muted }}>
            {editingBlocked
              ? tr('pflege.schedule.locked_blocked')
              : tr('pflege.schedule.locked_editable')}
          </div>
        )}
        {formError && (
          <div role="alert" style={{ marginTop: 12, padding: '9px 12px', background: dark ? '#1a0505' : '#fff5f5', border: '1px solid #fda29b', borderRadius: 8, fontSize: 12.5, color: '#b42318' }}>
            {formError}
          </div>
        )}
      </Card>

      {!isMobile && stickyPalette}

      {sortedBlocks.length === 0 && (
        <Card dark={dark} style={{ padding: 20 }}>
          <div style={{ fontSize: 13, color: tc.muted }}>{tr('pflege.schedule.empty')}</div>
        </Card>
      )}

      {sortedBlocks.map((b, i) => {
        const pause = isPauseBlock(b);
        const draggingThis = listDrag?.key === b._key;
        // Einfügemarke: an der Zielposition in der Liste OHNE den gezogenen Eintrag.
        const otherIdx = listDrag ? sortedBlocks.slice(0, i).filter(x => x._key !== listDrag.key).length : -1;
        const showMarkerBefore = !!listDrag && !draggingThis && listDrag.over !== listDrag.from && listDrag.over === otherIdx;
        const showMarkerAfter = !!listDrag && listDrag.over !== listDrag.from && i === sortedBlocks.length - 1
          && listDrag.over === sortedBlocks.length - 1 && !draggingThis;
        const handle = !editingBlocked && sortedBlocks.length > 1 && (
          <button
            type="button"
            aria-label={tr('pflege.schedule.moveHandle')}
            title={tr('pflege.schedule.moveHandleTitle')}
            data-testid="schedule-entry-handle"
            onPointerDown={e => onHandlePointerDown(e, b._key, i)}
            onPointerMove={e => onHandlePointerMove(e, b._key)}
            onPointerUp={e => onHandlePointerUp(e, b._key)}
            onPointerCancel={() => setListDrag(null)}
            onKeyDown={e => onHandleKeyDown(e, b._key, i)}
            onClick={e => e.stopPropagation()}
            style={{
              flexShrink: 0, alignSelf: 'stretch', width: 26, padding: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'transparent', border: 'none', borderRadius: 6,
              color: tc.muted, fontSize: 15, lineHeight: 1,
              cursor: draggingThis ? 'grabbing' : 'grab',
              touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none',
            }}
          >
            <span aria-hidden>⠿</span>
          </button>
        );
        const marker = (
          <div aria-hidden data-testid="schedule-entry-drop-marker" style={{ height: 3, borderRadius: 2, background: BRAND, margin: '-7px 4px 4px' }} />
        );
        return (
        <React.Fragment key={b._key}>
        {showMarkerBefore && marker}
        <div
          ref={el => { if (el) cardRefs.current.set(b._key, el); else cardRefs.current.delete(b._key); }}
          data-testid="schedule-entry"
          style={{
            borderRadius: 12, outline: b._key === selectedKey ? `2px solid ${BRAND}` : 'none', outlineOffset: 2,
            transition: 'outline-color 0.2s', opacity: draggingThis ? 0.5 : 1,
          }}
        >
        {b._key !== expandedKey ? (
          // Kompakt: nur Tätigkeit, Startzeit und Dauer — Klick öffnet die volle Ansicht.
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 2, borderRadius: 12,
              background: tc.card, border: `1px solid ${tc.border}`,
              borderLeft: `3px solid ${pause ? '#16a34a' : BRAND}`,
              paddingLeft: handle ? 4 : 0,
            }}
          >
            {handle}
            <button
              type="button"
              aria-expanded={false}
              onClick={() => { setExpandedKey(b._key); setSelectedKey(b._key); }}
              style={{
                flex: 1, minWidth: 0, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10,
                padding: handle ? '10px 14px 10px 4px' : '10px 14px', borderRadius: 12, cursor: 'pointer', fontFamily: 'Outfit,sans-serif',
                background: 'transparent', border: 'none',
              }}
            >
              <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: b.care_type ? tc.heading : tc.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {b.care_type ? activityLabel(b.care_type, tr) : tr('pflege.schedule.activityPlaceholder')}
              </span>
              <span style={{ fontSize: 12.5, color: tc.text, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{b.start_time}</span>
              <span style={{ fontSize: 12, color: tc.muted, whiteSpace: 'nowrap' }}>{fmtHm(blockMinutes(b))}</span>
              <span aria-hidden style={{ color: tc.muted, fontSize: 16, lineHeight: 1 }}>›</span>
            </button>
          </div>
        ) : (
        <Card dark={dark} style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* Kopfbereich: leicht schraffiert abgesetzt, Klick klappt den Eintrag zu. */}
          <div
            data-testid="schedule-entry-header"
            style={{
              display: 'flex', alignItems: 'center', gap: 2,
              paddingLeft: handle ? 4 : 0,
              borderBottom: `1px solid ${tc.border}`,
              borderLeft: `3px solid ${pause ? '#16a34a' : BRAND}`,
              background: pause
                ? `repeating-linear-gradient(45deg, rgba(34,197,94,${dark ? '0.10' : '0.06'}) 0 6px, rgba(34,197,94,${dark ? '0.18' : '0.12'}) 6px 12px)`
                : `repeating-linear-gradient(45deg, rgba(175,0,124,${dark ? '0.08' : '0.04'}) 0 6px, rgba(175,0,124,${dark ? '0.16' : '0.09'}) 6px 12px)`,
            }}
          >
            {handle}
            <button
              type="button"
              aria-expanded
              aria-label={tr('pflege.schedule.collapseHeader')}
              title={tr('pflege.schedule.collapseHeader')}
              onClick={() => setExpandedKey(null)}
              style={{
                flex: 1, minWidth: 0, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10,
                padding: handle ? '10px 14px 10px 4px' : '10px 14px', cursor: 'pointer', fontFamily: 'Outfit,sans-serif',
                background: 'transparent', border: 'none',
              }}
            >
              <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: b.care_type ? tc.heading : tc.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {b.care_type ? activityLabel(b.care_type, tr) : tr('pflege.schedule.activityPlaceholder')}
              </span>
              <span style={{ fontSize: 12.5, color: tc.text, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{b.start_time}–{b.end_time}</span>
              <span aria-hidden style={{ color: tc.muted, fontSize: 16, lineHeight: 1, display: 'inline-block', transform: 'rotate(90deg)' }}>›</span>
            </button>
          </div>
          <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Tätigkeit */}
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: tc.muted }}>{tr('pflege.schedule.activity')}</span>
            <select
              aria-label={tr('pflege.schedule.activity')}
              value={b.care_type ?? ''}
              onChange={e => setActivity(b._key, e.target.value)}
              style={inputStyle}
            >
              <option value="">{tr('pflege.schedule.activityPlaceholder')}</option>
              {PB_ACTIVITIES.map(a => (
                <option key={a} value={a}>{activityLabel(a, tr)}</option>
              ))}
            </select>
          </label>

          {/* Zeit von–bis + Dauer (Bis bei Pause/Medikamente automatisch) */}
          {(() => {
            const fixed = hasFixedDuration(b.care_type);
            return (
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 100 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: tc.muted }}>{tr('pflege.schedule.from')}</span>
              <TimeInput24
                ariaLabel={tr('pflege.schedule.from')}
                value={b.start_time}
                onChange={v => setStart(b._key, v)}
                style={inputStyle}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 100 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: tc.muted }}>
                {tr('pflege.schedule.to')}
                {fixed && <span style={{ color: tc.muted, fontWeight: 400 }}> · {tr('pflege.schedule.autoEnd')}</span>}
              </span>
              <TimeInput24
                ariaLabel={tr('pflege.schedule.to')}
                value={b.end_time}
                disabled={fixed}
                onChange={v => setEnd(b._key, v)}
                style={{ ...inputStyle, opacity: fixed ? 0.6 : 1, cursor: fixed ? 'not-allowed' : 'auto' }}
              />
            </label>
            <div style={{ fontSize: 12.5, color: tc.muted, paddingBottom: 9, whiteSpace: 'nowrap' }}>
              {tr('pflege.schedule.duration')}: <span style={{ color: tc.text, fontWeight: 600 }}>{fmtHm(blockMinutes(b))}</span>
            </div>
          </div>
            );
          })()}

          {/* Beschreibung */}
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: tc.muted }}>{tr('pflege.schedule.note')}</span>
            <input
              type="text"
              aria-label={tr('pflege.schedule.note')}
              maxLength={NOTE_MAX_LEN}
              value={b.note ?? ''}
              placeholder={tr('pflege.schedule.notePlaceholder')}
              onChange={e => updateBlock(b._key, { note: e.target.value || null })}
              style={inputStyle}
            />
          </label>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button
              type="button"
              aria-expanded
              onClick={() => setExpandedKey(null)}
              style={{ fontSize: 12, padding: '6px 12px', background: 'transparent', border: `1px solid ${tc.border}`, borderRadius: 7, color: tc.text, cursor: 'pointer', fontFamily: 'Outfit,sans-serif' }}
            >
              {tr('pflege.schedule.collapse')}
            </button>
            <button
              type="button"
              onClick={() => removeBlock(b._key)}
              style={{ fontSize: 12, padding: '6px 12px', background: dark ? '#1a0505' : '#fff5f5', border: '1px solid #fecdca', borderRadius: 7, color: '#b42318', cursor: 'pointer', fontFamily: 'Outfit,sans-serif' }}
            >
              {tr('pflege.schedule.delete')}
            </button>
          </div>
          </div>
        </Card>
        )}
        </div>
        {showMarkerAfter && marker}
        </React.Fragment>
        );
      })}

      <button
        type="button"
        onClick={addBlock}
        style={{ fontSize: 13, fontWeight: 600, padding: '10px 12px', background: 'transparent', border: `1px dashed ${tc.border}`, borderRadius: 8, color: BRAND, cursor: 'pointer', fontFamily: 'Outfit,sans-serif' }}
      >
        + {tr('pflege.schedule.addActivity')}
      </button>
    </div>
  );

  /* ── Layout ──────────────────────────────────────────────────────────── */
  if (loadError) {
    return (
      <Card dark={dark} style={{ padding: 24 }}>
        <div style={{ fontSize: 13, color: '#b42318' }}>{loadError}</div>
      </Card>
    );
  }
  if (loading) {
    return (
      <Card dark={dark} style={{ padding: 24 }}>
        <div style={{ fontSize: 13, color: tc.muted }}>{tr('common.loading_short')}</div>
      </Card>
    );
  }

  return (
    // KEIN flex:1/minHeight:0 hier: der Inhalt definiert die Höhe, damit die
    // Tagesübersicht (position: sticky) beim Scrollen des Tab-Containers
    // wirklich haften bleibt — mit auf Sichthöhe geklemmter Spalte hätte
    // sticky keinen Laufweg.
    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 14 : 20 }}>
      {user?.id != null && (
        <ScheduleLinkPanel
          userId={user.id}
          accountId={(user as { family_account_id?: number | null }).family_account_id ?? null}
          dark={dark}
          onLockChange={setLocked}
        />
      )}
      {isMobile && stickyPalette}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'minmax(0, 360px) minmax(0, 1fr)',
          gap: isMobile ? 14 : 20,
          alignItems: 'start',
        }}
      >
        {overview}
        {entries}
      </div>
      <ScheduleEntryDrawer
        draft={draft}
        dark={dark}
        error={drawerError}
        noteRequired={draftNoteRequired}
        onActivity={draftSetActivity}
        onStart={draftSetStart}
        onPatch={draftPatch}
        onSave={saveDraft}
        onDelete={deleteDraft}
        onClose={closeDrawer}
      />
    </div>
  );
}
